/**
 * Pull a PDF member out of an uploaded ZIP so Digitize can preview it.
 * ABDP writes SOURCE_FILE_{JD} as the member file name.
 *
 * Labelled copies wrap a non-PDF source as `{original}.labelled.zip` containing
 * the original bytes plus COPY-LABEL.txt. Unwrap that inner ZIP so PayZen
 * batches still show each PDF.
 */

const MAX_UNWRAP = 3;
const ZIP_MAGIC = [0x50, 0x4b];

function basename(path) {
  const raw = String(path || '').replace(/\\/g, '/');
  const parts = raw.split('/').filter(Boolean);
  return parts[parts.length - 1] || raw;
}

function isPdfPath(path) {
  return /\.pdf$/i.test(String(path || ''));
}

function isZipPath(path) {
  return /\.zip$/i.test(String(path || ''));
}

function isCopyLabel(path) {
  return /^COPY-LABEL\.txt$/i.test(basename(path));
}

function hasZipMagic(bytes) {
  return Boolean(bytes && bytes.length >= 2 && bytes[0] === ZIP_MAGIC[0] && bytes[1] === ZIP_MAGIC[1]);
}

export function isZipSource({ mimeType, fileName, blob } = {}) {
  const mime = String(mimeType || blob?.type || '').toLowerCase();
  if (mime.includes('zip')) return true;
  return typeof fileName === 'string' && /\.zip$/i.test(fileName.trim());
}

function pickMember(members, wantedName) {
  if (!members.length) return null;
  const wanted = basename(wantedName).toLowerCase();
  if (!wanted) return members[0];
  const exact = members.find((m) => basename(m.path).toLowerCase() === wanted);
  if (exact) return exact;
  return members.find((m) => m.path.toLowerCase().endsWith(`/${wanted}`)) || null;
}

export const zipMemberBasename = basename;

/**
 * Choose one member path out of a server-provided list (same rules as the
 * client-side extraction: exact basename, then `/name` suffix, else first when
 * nothing was requested).
 * @param {string[]} memberPaths
 * @param {string} wantedName
 * @returns {string|null}
 */
export function pickZipMember(memberPaths, wantedName) {
  const members = (Array.isArray(memberPaths) ? memberPaths : [])
    .filter((path) => typeof path === 'string' && path)
    .map((path) => ({ path }));
  const chosen = pickMember(members, wantedName);
  return chosen ? chosen.path : null;
}

function listFiles(zip) {
  const files = [];
  zip.forEach((relativePath, file) => {
    if (file.dir) return;
    if (relativePath.startsWith('__MACOSX/')) return;
    if (isCopyLabel(relativePath)) return;
    files.push({ path: relativePath, file });
  });
  return files;
}

async function collectPdfMembers(JSZip, zip, depth = 0) {
  const files = listFiles(zip);
  const members = files.filter((item) => isPdfPath(item.path));
  if (members.length || depth >= MAX_UNWRAP) return members;

  const nested = [];
  for (const item of files) {
    if (isZipPath(item.path)) {
      nested.push(item);
      continue;
    }
    const head = await item.file.async('uint8array');
    if (hasZipMagic(head)) nested.push(item);
  }

  const unwrapped = [];
  for (const inner of nested) {
    try {
      const bytes = await inner.file.async('uint8array');
      const innerZip = await JSZip.loadAsync(bytes);
      const found = await collectPdfMembers(JSZip, innerZip, depth + 1);
      unwrapped.push(...found);
    } catch {
      // Not a readable ZIP member — keep looking.
    }
  }
  return unwrapped;
}

/**
 * @returns {Promise<{ blob: Blob, fileName: string, members: string[] }>}
 */
export async function extractPdfFromZip(sourceBlob, memberName) {
  if (!sourceBlob) {
    throw new Error('ZIP source file could not be loaded.');
  }
  const { default: JSZip } = await import('jszip');
  const zip = await JSZip.loadAsync(sourceBlob);
  const members = await collectPdfMembers(JSZip, zip);
  if (!members.length) {
    throw new Error('This ZIP does not contain any PDF files.');
  }
  const chosen = pickMember(members, memberName);
  if (!chosen) {
    const names = members.map((m) => basename(m.path)).join(', ');
    throw new Error(
      memberName
        ? `No PDF named ${basename(memberName)} in this ZIP. Found: ${names}.`
        : `Select a classified document to open one of: ${names}.`
    );
  }
  const bytes = await chosen.file.async('uint8array');
  return {
    blob: new Blob([bytes], { type: 'application/pdf' }),
    fileName: basename(chosen.path),
    members: members.map((m) => basename(m.path)),
  };
}
